import React, { useEffect, useState } from 'react';
import { Checkbox, FormControlLabel, IconButton, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { makeStyles } from 'tss-react/mui';
import { Controller, UseFormSetValue, useFieldArray, useForm, useWatch } from 'react-hook-form';
import {
  PermissionsGroupRole,
  SchemaCatalogBody,
  SchemaCatalogResponse,
  SchemaFieldDefinition,
  SchemaFieldType
} from '../../../API';
import { useSchemasAPI } from '../../../hooks/API/Config/useSchemasAPI';
import { useLocales, useNotifications, useTheme } from '../../../hooks';
import { useRecoilValue } from 'recoil';
import { withCurrentUser } from '../../../state/auth';
import Button from '../../shared/Button';
import Drawer from '../../shared/Drawer';

const useStyles = makeStyles()((theme) => ({
  formBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
    padding: theme.spacing(4)
  },
  fieldsSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3)
  },
  fieldRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 160px 80px 40px',
    gap: theme.spacing(2),
    alignItems: 'center'
  },
  optionsRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    paddingLeft: theme.spacing(4)
  },
  optionItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2)
  },
  footerButtons: {
    display: 'flex',
    gap: theme.spacing(4)
  },
  footerButton: {
    minWidth: 160
  },
  sectionLabel: {
    fontWeight: 600
  }
}));

interface FieldRow {
  key: string;
  type: SchemaFieldType;
  required: boolean;
  options: string[];
}

interface FormValues {
  name: string;
  fields: FieldRow[];
}

const DEFAULT_FIELD: FieldRow = {
  key: '',
  type: SchemaFieldType.TEXT,
  required: false,
  options: []
};

interface SchemaFormProps {
  open: boolean;
  schema?: SchemaCatalogResponse;
  onClose: (saved: boolean) => void;
}

function SchemaForm({ open, schema, onClose }: SchemaFormProps): JSX.Element {
  const { classes } = useStyles();
  const { t } = useLocales();
  const { notifyError } = useNotifications();
  const schemasAPI = useSchemasAPI();
  const currentUser = useRecoilValue(withCurrentUser);
  const isEditing = !!schema;
  const { formControlColor } = useTheme();
  const [isSaving, setIsSaving] = useState(false);
  const canSaveSchema =
    currentUser?.permissionsGroups?.some(
      (permissionsGroup) => permissionsGroup.group === 'ADMIN' && permissionsGroup.role === PermissionsGroupRole.ADMIN
    ) ||
    currentUser?.permissionsGroups?.some(
      (permissionsGroup) =>
        permissionsGroup.group === 'DYNAMIC_MODULES' && permissionsGroup.role === PermissionsGroupRole.ADMIN
    );

  const { control, handleSubmit, reset, setValue } = useForm<FormValues>({
    defaultValues: { name: '', fields: [{ ...DEFAULT_FIELD }] }
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'fields' });

  useEffect(() => {
    if (open) {
      if (schema) {
        const fieldRows: FieldRow[] = Object.entries(schema.fields).map(([key, def]) => {
          const d = def as SchemaFieldDefinition;
          return { key, type: d.type, required: d.required, options: d.options ?? [] };
        });
        reset({ name: schema.name, fields: fieldRows.length > 0 ? fieldRows : [{ ...DEFAULT_FIELD }] });
      } else {
        reset({ name: '', fields: [{ ...DEFAULT_FIELD }] });
      }
    }
  }, [open, schema, reset]);

  const onSubmit = async (values: FormValues) => {
    const fieldErrors: string[] = [];
    const keys = values.fields.map((f) => (f?.key ?? '').trim());
    keys.forEach((k, i) => {
      if (!k) fieldErrors.push(t('schemas.field_key_required', { index: i + 1 }));
    });
    const uniqueKeys = new Set(keys.filter(Boolean));
    if (uniqueKeys.size !== keys.filter(Boolean).length) {
      fieldErrors.push(t('schemas.field_keys_must_be_unique'));
    }
    values.fields.forEach((f, i) => {
      if (f.type === SchemaFieldType.SELECT && f.options.filter((o) => o.trim()).length === 0) {
        fieldErrors.push(t('schemas.field_select_requires_option', { index: i + 1 }));
      }
    });
    if (fieldErrors.length > 0) {
      notifyError(fieldErrors.join(' · '));
      return;
    }

    const fieldsRecord: Record<string, SchemaFieldDefinition> = {};
    values.fields.forEach((f) => {
      const key = f.key.trim();
      fieldsRecord[key] = {
        type: f.type,
        required: f.required,
        ...(f.type === SchemaFieldType.SELECT ? { options: f.options.filter((o) => o.trim()) } : {})
      };
    });

    const body: SchemaCatalogBody = { name: values.name.trim(), fields: fieldsRecord };

    setIsSaving(true);
    try {
      if (isEditing && schema) {
        await schemasAPI.update(schema.id, body);
      } else {
        await schemasAPI.create(body);
      }
      onClose(true);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 409) {
        notifyError(t('schemas.error_duplicate_name'));
      } else {
        notifyError(t('schemas.error_save_failed'));
      }
    } finally {
      setIsSaving(false);
    }
  };

  const footerLeft = (
    <div className={classes.footerButtons}>
      <Button
        className={classes.footerButton}
        color="primary"
        loading={isSaving}
        onClick={handleSubmit(onSubmit)}
        disabled={!canSaveSchema}
      >
        {t('general.save')}
      </Button>
    </div>
  );
  const footerRight = (
    <Button className={classes.footerButton} color="grey" onClick={() => onClose(false)}>
      {t('general.cancel')}
    </Button>
  );

  return (
    <Drawer
      open={open}
      onClose={() => onClose(false)}
      headerLeft={
        <Typography variant="h6">
          {isEditing ? t('schemas.edit_schema', { name: schema?.name }) : t('schemas.new_schema')}
        </Typography>
      }
      footerLeft={footerLeft}
      footerRight={footerRight}
    >
      <div className={classes.formBody}>
        <Controller
          control={control}
          name="name"
          rules={{ required: t('schemas.schema_name_required') }}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              label={t('schemas.schema_name')}
              required
              error={!!error}
              helperText={error?.message}
              fullWidth
              color={formControlColor}
            />
          )}
        />

        <div className={classes.fieldsSection}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle2" className={classes.sectionLabel}>
              {t('schemas.fields_section')}
              {fields.length === 0 && <span style={{ color: 'red' }}>{t('schemas.fields_at_least_one')}</span>}
            </Typography>
            <Button size="small" startIcon={<Add />} onClick={() => append({ ...DEFAULT_FIELD })}>
              {t('schemas.add_field')}
            </Button>
          </Stack>

          {fields.map((field, index) => (
            <div key={field.id}>
              <div className={classes.fieldRow}>
                <Controller
                  control={control}
                  name={`fields.${index}.key`}
                  render={({ field: f, fieldState: { error } }) => (
                    <TextField
                      {...f}
                      label={t('schemas.field_key')}
                      size="small"
                      placeholder={t('schemas.field_key_placeholder')}
                      fullWidth
                      autoComplete="off"
                      color={formControlColor}
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name={`fields.${index}.type`}
                  render={({ field: f }) => (
                    <Select {...f} size="small" fullWidth color={formControlColor}>
                      {(Object.values(SchemaFieldType) as string[]).map((type) => (
                        <MenuItem key={type} value={type} color={formControlColor}>
                          {type}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
                <Controller
                  control={control}
                  name={`fields.${index}.required`}
                  render={({ field: f }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          color={formControlColor}
                          checked={f.value}
                          onChange={(e) => f.onChange(e.target.checked)}
                          size="small"
                        />
                      }
                      label={t('schemas.field_required_abbr')}
                    />
                  )}
                />
                <IconButton
                  size="small"
                  color={formControlColor}
                  onClick={() => remove(index)}
                  disabled={fields.length === 1}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </div>

              <OptionsEditor control={control} fieldIndex={index} setValue={setValue} classes={classes} />
            </div>
          ))}
        </div>
      </div>
    </Drawer>
  );
}

function OptionsEditor({
  control,
  fieldIndex,
  setValue,
  classes
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: any;
  fieldIndex: number;
  setValue: UseFormSetValue<FormValues>;
  classes: ReturnType<typeof useStyles>['classes'];
}) {
  const { t } = useLocales();
  const typeField = useTypeWatcher(control, fieldIndex);
  const { formControlColor } = useTheme();
  const options = (useWatch({ control, name: `fields.${fieldIndex}.options` }) as string[]) ?? [];

  if (typeField !== SchemaFieldType.SELECT) return null;

  const addOption = () => {
    setValue(`fields.${fieldIndex}.options`, [...options, '']);
  };

  const removeOption = (optIndex: number) => {
    setValue(
      `fields.${fieldIndex}.options`,
      options.filter((_, i) => i !== optIndex)
    );
  };

  const updateOption = (optIndex: number, value: string) => {
    const updated = [...options];
    updated[optIndex] = value;
    setValue(`fields.${fieldIndex}.options`, updated);
  };

  return (
    <div className={classes.optionsRow}>
      <Typography variant="caption" color="textSecondary">
        {t('schemas.options_for_select')}
      </Typography>
      {options.map((opt, optIndex) => (
        <div key={optIndex} className={classes.optionItem}>
          <TextField
            value={opt}
            size="small"
            placeholder={`Option ${optIndex + 1}`}
            sx={{ flexGrow: 1 }}
            onChange={(e) => updateOption(optIndex, e.target.value)}
            color={formControlColor}
          />
          <IconButton size="small" color={formControlColor} onClick={() => removeOption(optIndex)}>
            <Delete fontSize="small" />
          </IconButton>
        </div>
      ))}
      <Button
        size="small"
        color={formControlColor}
        startIcon={<Add />}
        onClick={addOption}
        sx={{ alignSelf: 'flex-start' }}
      >
        {t('schemas.add_option')}
      </Button>
    </div>
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function useTypeWatcher(control: any, fieldIndex: number): SchemaFieldType {
  const type = useWatch({ control, name: `fields.${fieldIndex}.type` });
  return (type as SchemaFieldType) ?? SchemaFieldType.TEXT;
}

export default SchemaForm;
